import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, Home, Crown, Zap, Star, Users, TrendingUp, Award, Shield, Clock, Target, MessageSquare } from 'lucide-react';
import Header from './Header';
import { motion } from 'framer-motion';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [currentPlan, setCurrentPlan] = useState('basic');
  const [userInfo, setUserInfo] = useState(null);
  const [userRole, setUserRole] = useState('user');

  useEffect(() => {
    const loadUserInfo = () => {
      try {
        const storedUserInfo = localStorage.getItem('userInfo');
        const storedUserRole = localStorage.getItem('userRole') || 'user';
        const selectedEngine = localStorage.getItem('selectedEngine') || 'daily-news-analysis';
        
        if (storedUserInfo) {
          const parsed = JSON.parse(storedUserInfo);
          setUserInfo(parsed);
        }
        setUserRole(storedUserRole);
        setCurrentPlan(selectedEngine);
      } catch (error) {
        console.error('사용자 정보 로드 실패:', error);
      }
    };
    
    loadUserInfo();
  }, []);

  const plans = [
    {
      id: 'daily-news-analysis',
      name: '일일 뉴스 분석',
      price: '무료',
      description: '뉴스 기사 분석 및 요약을 위한\n기본 AI 서비스',
      monthlyTokens: '10,000',
      features: [
        '월 10,000 토큰',
        '뉴스 기사 요약',
        '키워드 추출',
        '감정 분석',
        '기본 보고서 생성',
        '이메일 지원'
      ],
      popular: false,
      ctaText: '무료로 시작'
    },
    {
      id: 'press-release-writing',
      name: '보도자료 작성',
      price: '₩49,000',
      originalPrice: '₩69,000',
      description: '전문 보도자료 작성을 위한\n고급 AI 서비스',
      monthlyTokens: '50,000',
      features: [
        '월 50,000 토큰',
        'AI 보도자료 작성',
        '다양한 템플릿 제공',
        'SEO 최적화',
        '멀티미디어 지원',
        '우선 기술 지원',
        '무제한 수정 및 저장'
      ],
      popular: true,
      ctaText: '14일 무료 체험'
    },
    {
      id: 'digital-news-writing',
      name: '디지털 뉴스 작성',
      price: '₩99,000',
      originalPrice: '₩129,000',
      description: '언론사 및 기업을 위한\n최고급 AI 뉴스 작성 서비스',
      monthlyTokens: '200,000',
      features: [
        '월 200,000 토큰',
        '실시간 뉴스 생성',
        '다국어 지원',
        'API 무제한 접근',
        '전담 계정 관리자',
        '팀 협업 도구',
        '고급 분석 대시보드',
        '맞춤형 AI 모델 튜닝'
      ],
      popular: false,
      ctaText: '30일 무료 체험'
    }
  ];

  const testimonials = [
    {
      name: '김지연',
      role: '디지털 콘텐츠 팀장, 서울경제',
      comment: 'AI 뉴스 작성 서비스로 기사 생산성이 3배 향상되었습니다. 품질도 뛰어나요.',
      plan: '디지털 뉴스 작성'
    },
    {
      name: '박민수',
      role: '홍보팀장, IT 스타트업',
      comment: '보도자료 작성 시간이 80% 단축되었고, 언론사 게재율도 크게 높아졌습니다.',
      plan: '보도자료 작성'
    },
    {
      name: '이소영',
      role: '마케팅 매니저',
      comment: '일일 뉴스 분석으로 시장 트렌드를 빠르게 파악할 수 있어 전략 수립에 큰 도움이 됩니다.',
      plan: '일일 뉴스 분석'
    }
  ];

  const handleBack = () => {
    navigate(-1);
  };

  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('userRole');
    localStorage.removeItem('selectedEngine');
    localStorage.removeItem('userInfo');
    localStorage.removeItem('authToken');
    localStorage.removeItem('idToken');
    localStorage.removeItem('refreshToken');
    navigate('/');
  };

  const handleUpgrade = (planId) => {
    if (planId === currentPlan) return;
    
    localStorage.setItem('selectedEngine', planId);
    setCurrentPlan(planId);
    
    setTimeout(() => {
      navigate(`/${planId}`);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Custom Header */}
      <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-sm border-b border-slate-200/60">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors font-medium"
            >
              <Home size={20} />
              <span>홈으로 돌아가기</span>
            </button>
            
            {userInfo && (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-900 rounded-full flex items-center justify-center text-white text-sm font-medium">
                  {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <span className="text-sm text-slate-600 hidden sm:inline font-medium">
                  {userInfo?.name || userInfo?.email}
                </span>
              </div>
            )}
          </div>
        </div>
      </header>
      
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        {/* Hero Section */}
        <div className="pt-20 pb-32 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Zap className="w-4 h-4" />
              <span>AI 기반 뉴스 콘텐츠 생성 서비스</span>
            </div>
            
            <h1 className="text-6xl lg:text-7xl font-bold text-slate-900 mb-8 tracking-tight leading-[1.1]">
              AI가 만드는
              <br />
              <span className="bg-gradient-to-r from-slate-900 to-slate-600 bg-clip-text text-transparent">
                뉴스의 미래
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-slate-600 max-w-3xl mx-auto leading-relaxed mb-16 font-light">
              실시간 뉴스 분석부터 보도자료 작성까지, AI 기술로 언론의 디지털 혁신을
              <br className="hidden sm:block" />
              <span className="text-slate-900 font-medium">더 빠르고 정확하게</span> 실현하세요
            </p>
            
            <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16 text-slate-500">
              <div className="flex items-center gap-3 bg-white/60 backdrop-blur-sm px-4 py-3 rounded-full border border-slate-200/50">
                <Users className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-700">100+ 언론사 이용</span>
              </div>
              <div className="flex items-center gap-3 bg-white/60 backdrop-blur-sm px-4 py-3 rounded-full border border-slate-200/50">
                <Shield className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-700">14일 무료 체험</span>
              </div>
              <div className="flex items-center gap-3 bg-white/60 backdrop-blur-sm px-4 py-3 rounded-full border border-slate-200/50">
                <Clock className="w-5 h-5 text-slate-600" />
                <span className="font-medium text-slate-700">24시간 지원</span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Current Plan Status */}
        {userInfo && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-slate-50/90 to-slate-100/50 rounded-3xl p-10 mb-32 border border-slate-200/60 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-6">
                <div className="w-14 h-14 bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl flex items-center justify-center text-white font-semibold shadow-lg">
                  {userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-1">
                    현재 플랜: {plans.find(plan => plan.id === currentPlan)?.name}
                  </h3>
                  <p className="text-slate-600">
                    {userInfo?.name || userInfo?.email}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-slate-500 mb-1">다음 결제일</div>
                <div className="font-semibold text-slate-900">
                  {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('ko-KR', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Value Proposition */}
        <div className="mb-32">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Target className="w-4 h-4" />
              <span>핵심 가치</span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              왜 AI 뉴스 작성이
              <br />
              필요한가요?
            </h2>
            <p className="text-xl lg:text-2xl text-slate-600 max-w-4xl mx-auto font-light leading-relaxed">
              빠른 뉴스 생산과 더불어 전문성과 정확성을 함께 확보하세요
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-10 mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="group bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-3xl p-10 hover:border-slate-300/80 hover:bg-white hover:shadow-lg transition-all duration-500"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl flex items-center justify-center mb-8 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <Award className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6 leading-tight">
                실시간 뉴스 분석
              </h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                <span className="font-semibold text-slate-900">24시간 실시간</span>으로 국내외 뉴스를 분석하고,{' '}
                <span className="font-semibold text-slate-900">핵심 키워드</span>와 트렌드를 파악하여 
                빠르고 정확한 의사결정을 도와드립니다.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="group bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-3xl p-10 hover:border-slate-300/80 hover:bg-white hover:shadow-lg transition-all duration-500"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl flex items-center justify-center mb-8 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <Shield className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6 leading-tight">
                AI 보도자료 작성
              </h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                전문 기자의 작성 패턴을 학습한 AI가 <span className="font-semibold text-slate-900">맞춤형 보도자료</span>를{' '}
                <span className="font-semibold text-slate-900">자동 생성</span>합니다. 
                언론사별 문체, 형식, 규정에 맞춰 최적화된 결과물을 제공합니다.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="group bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-3xl p-10 hover:border-slate-300/80 hover:bg-white hover:shadow-lg transition-all duration-500"
            >
              <div className="w-14 h-14 bg-gradient-to-br from-slate-900 to-slate-700 rounded-2xl flex items-center justify-center mb-8 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                <TrendingUp className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-6 leading-tight">
                효율적인 콘텐츠 생산
              </h3>
              <p className="text-slate-600 leading-relaxed text-lg">
                기사 작성 시간을 <span className="font-semibold text-slate-900">80% 단축</span>하고{' '}
                동시에 <span className="font-semibold text-slate-900">전문성과 품질</span>을 
                높여 언론사의 경쟁력을 강화합니다.
              </p>
            </motion.div>
          </div>

          {/* Before/After Comparison */}
          <div className="bg-slate-50/80 rounded-3xl p-12 border border-slate-200/50">
            <div className="text-center mb-12">
              <h3 className="text-3xl font-bold text-slate-900 mb-4">
                AI 뉴스 작성이 만드는 차이
              </h3>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <span className="text-red-600 text-lg font-bold">×</span>
                  </div>
                  <h4 className="text-xl font-semibold text-slate-900">기존 방식</h4>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3 text-slate-600">
                    <span className="text-red-500 mt-1">•</span>
                    <span>수동 기사 작성으로 오랜 시간 소요</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <span className="text-red-500 mt-1">•</span>
                    <span>트렌드 파악에 어려움</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <span className="text-red-500 mt-1">•</span>
                    <span>전문 용어 사용이 부정확</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <span className="text-red-500 mt-1">•</span>
                    <span>AI 특유의 어색한 표현</span>
                  </li>
                </ul>
              </div>
              
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <Check size={20} className="text-green-600" />
                  </div>
                  <h4 className="text-xl font-semibold text-slate-900">AI 뉴스 작성</h4>
                </div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3 text-slate-600">
                    <Check size={16} className="text-green-500 mt-1 flex-shrink-0" />
                    <span>실시간 트렌드 분석 기반 기사 작성</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <Check size={16} className="text-green-500 mt-1 flex-shrink-0" />
                    <span>언론사별 맞춤형 문체 적용</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <Check size={16} className="text-green-500 mt-1 flex-shrink-0" />
                    <span>SEO 최적화된 콘텐츠 생성</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <Check size={16} className="text-green-500 mt-1 flex-shrink-0" />
                    <span>다양한 형식의 보도자료 자동 생성</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Plans */}
        <div className="mb-32">
          <div className="text-center mb-20">
            <div className="inline-flex items-center gap-2 bg-slate-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Crown className="w-4 h-4" />
              <span>구독 플랜</span>
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              플랜을 선택하세요
            </h2>
            <p className="text-xl lg:text-2xl text-slate-600 font-light">
              당신의 필요에 맞는 최적의 플랜을 찾아보세요
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-10">
            {plans.map((plan, index) => (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`group relative bg-white/90 backdrop-blur-sm rounded-[2rem] p-10 transition-all duration-500 ${
                  plan.popular 
                    ? 'border-2 border-slate-900 shadow-2xl scale-105 bg-white' 
                    : plan.id === currentPlan
                    ? 'border-2 border-green-500 shadow-2xl bg-white'
                    : 'border border-slate-200/60 hover:border-slate-300/80 hover:shadow-2xl hover:scale-[1.02] hover:bg-white'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-slate-900 to-slate-700 text-white px-8 py-3 rounded-full text-sm font-semibold shadow-lg">
                      🔥 가장 인기
                    </div>
                  </div>
                )}

                <div className="text-center mb-10">
                  <h3 className="text-3xl font-bold text-slate-900 mb-4">{plan.name}</h3>
                  <p className="text-slate-600 mb-8 leading-relaxed text-base px-2 whitespace-pre-line">{plan.description}</p>
                  
                  <div className="mb-8">
                    {plan.originalPrice && (
                      <div className="text-sm text-slate-400 line-through mb-1 text-center">
                        {plan.originalPrice}
                      </div>
                    )}
                    <div className="text-3xl lg:text-4xl font-bold text-slate-900 text-center whitespace-nowrap">
                      {plan.price}
                      {plan.price !== '무료' && (
                        <span className="text-lg text-slate-600 ml-1">/월</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-slate-500 mb-10 text-base">
                    월 {plan.monthlyTokens} 토큰 포함
                  </div>
                </div>

                <div className="space-y-5 mb-10">
                  {plan.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-4">
                      <Check size={18} className="text-green-500 mt-1 flex-shrink-0" />
                      <span className="text-slate-700 text-base">{feature}</span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={plan.id === currentPlan}
                  className={`w-full py-5 px-8 rounded-2xl font-semibold text-lg transition-all duration-300 ${
                    plan.id === currentPlan
                      ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                      : plan.popular
                      ? 'bg-gradient-to-r from-slate-900 to-slate-700 text-white hover:from-slate-800 hover:to-slate-600 hover:shadow-2xl transform hover:scale-[1.02]'
                      : 'bg-white border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white hover:shadow-2xl transform hover:scale-[1.02]'
                  }`}
                >
                  {plan.id === currentPlan ? '현재 플랜' : plan.ctaText}
                </button>
                
                {plan.id !== currentPlan && plan.price !== '무료' && (
                  <p className="text-xs text-slate-500 text-center mt-4">
                    카드 등록 없이 7일 무료 체험
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Social Proof */}
        <div className="mb-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              전문가들의 선택
            </h2>
            <p className="text-xl text-slate-600">
              이미 수많은 전문가들이 경험한 변화를 확인해보세요
            </p>
          </div>
          
          <div className="grid lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.2 }}
                className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-slate-300 transition-colors"
              >
                <div className="flex items-center gap-1 mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                
                <p className="text-slate-700 mb-6 leading-relaxed text-lg">
                  "{testimonial.comment}"
                </p>
                
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-600">{testimonial.role}</div>
                  </div>
                  <div className="text-xs bg-slate-100 text-slate-700 px-3 py-1 rounded-full font-medium">
                    {testimonial.plan}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Feature Comparison */}
        <div className="mb-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              플랜별 기능 비교
            </h2>
          </div>
          
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50/80">
                  <tr>
                    <th className="px-8 py-6 text-left text-lg font-semibold text-slate-900">기능</th>
                    <th className="px-8 py-6 text-center text-lg font-semibold text-slate-900">일일 뉴스 분석</th>
                    <th className="px-8 py-6 text-center text-lg font-semibold text-slate-900 bg-slate-100">보도자료 작성</th>
                    <th className="px-8 py-6 text-center text-lg font-semibold text-slate-900">디지털 뉴스 작성</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {[
                    { feature: '월간 토큰', daily: '10,000개', press: '50,000개', digital: '200,000개' },
                    { feature: '뉴스 분석', daily: '기본', press: '고급', digital: '전문가급' },
                    { feature: '기사 작성', daily: '요약', press: '보도자료', digital: '실시간 뉴스' },
                    { feature: '템플릿', daily: '✗', press: '✓', digital: '✓' },
                    { feature: 'SEO 최적화', daily: '✗', press: '✓', digital: '고급' },
                    { feature: '우선 지원', daily: '이메일', press: '✓', digital: '24시간' },
                    { feature: 'API 접근', daily: '✗', press: '✗', digital: '✓' },
                    { feature: '팀 기능', daily: '✗', press: '✗', digital: '✓' }
                  ].map((row, index) => (
                    <tr key={index} className="hover:bg-slate-50/50">
                      <td className="px-8 py-6 font-medium text-slate-900">{row.feature}</td>
                      <td className="px-8 py-6 text-center text-slate-600">{row.daily}</td>
                      <td className="px-8 py-6 text-center text-slate-600 bg-slate-50/50">{row.press}</td>
                      <td className="px-8 py-6 text-center text-slate-600">{row.digital}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mb-24">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              자주 묻는 질문
            </h2>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {[
              {
                q: '플랜을 언제든지 변경할 수 있나요?',
                a: '네, 언제든지 플랜을 업그레이드하거나 다운그레이드할 수 있습니다. 변경사항은 즉시 적용됩니다.'
              },
              {
                q: '7일 무료 체험은 어떻게 작동하나요?',
                a: '카드 등록 없이 7일 동안 선택한 플랜의 모든 기능을 체험할 수 있습니다. 체험 기간 중 언제든 취소 가능합니다.'
              },
              {
                q: '토큰이 무엇인가요?',
                a: '토큰은 AI 기사 작성 서비스 사용량을 측정하는 단위입니다. 일반적으로 한글 단어 1개당 1-2개의 토큰이 사용됩니다.'
              },
              {
                q: '환불 정책은 어떻게 되나요?',
                a: '결제 후 7일 이내 전액 환불이 가능합니다. 그 이후에는 사용량에 따라 부분 환불됩니다.'
              },
              {
                q: '기업용 플랜이 있나요?',
                a: '네, 기업용 맞춤 플랜을 제공합니다. 팀 관리, 대량 처리 등의 기능이 포함됩니다.'
              },
              {
                q: '데이터는 안전한가요?',
                a: 'SSL 암호화와 정기적인 보안 점검을 통해 고객 데이터를 안전하게 보호합니다.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-slate-300 transition-colors"
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-4">{faq.q}</h3>
                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-gradient-to-br from-slate-900 to-slate-800 rounded-[2rem] p-16 mb-20 shadow-2xl">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            지금 시작해보세요
          </h2>
          <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto font-light">
            전문가 수준의 AI 기사 작성 서비스로 당신의 뉴스 콘텐츠 생산을 완전히 다른 차원으로 끌어올리세요
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-lg mx-auto">
            <button
              onClick={() => handleUpgrade('press-release-writing')}
              className="bg-white text-slate-900 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-slate-100 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              14일 무료 체험 시작
            </button>
            <button
              onClick={() => handleUpgrade('daily-news-analysis')}
              className="border-2 border-white/80 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-slate-900 transition-all duration-300 transform hover:scale-105"
            >
              무료로 시작하기
            </button>
          </div>
          <p className="text-sm text-slate-400 mt-8 opacity-80">
            카드 등록 불필요 • 언제든 취소 가능 • 즉시 사용 가능
          </p>
        </div>
      </div>
      
      {/* Bottom Spacer */}
      <div className="h-20"></div>
    </div>
  );
};

export default SubscriptionPage;